import { useMemo, useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Calculator, ArrowRight, Sparkle, Users, User, Car, ShieldCheck, Tooth, Pill, Wheelchair, Share, Download } from "@phosphor-icons/react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { startDirectCheckout } from "@/lib/api";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import { QRCodeSVG } from "qrcode.react";
import {
  PROVINCIAL_RATES,
  FEDERAL_RATE,
  THRESHOLD_CAP as FEDERAL_CAP_2025,
} from "@/lib/taxCredit";

const fmtCAD = (v) =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 }).format(Number(v) || 0);

export default function TaxCalculatorWidget({ onEstimateChange } = {}) {
  const navigate = useNavigate();
  const shareCardRef = useRef(null);
  const [sharing, setSharing] = useState(false);

  const goSubscribe = async () => {
    try { await startDirectCheckout("yearly"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start checkout. Try again in a moment."); }
  };

  /**
   * shareResult — captures the off-screen share-card DOM node as a PNG and
   * pushes it through the native share sheet (iOS/Android) or downloads it on
   * desktop. The card itself is a sibling element kept positioned far
   * off-screen so it never flashes on render but is still painted (required
   * for html2canvas to capture).
   */
  const shareResult = async () => {
    if (!shareCardRef.current || sharing) return;
    setSharing(true);

    // Prepare share text up-front so we can still deliver *something* if the
    // image step fails (iOS Safari + html2canvas is fragile with modern CSS
    // color functions like oklch/color-mix or hsl(var(--…)) custom props).
    const amountStr = Math.round(calc.best.credit).toLocaleString("en-CA");
    const shareUrl = `https://mymedeasy.ca?ref=share&amount=${Math.round(calc.best.credit)}`;
    const shareText =
      `I'm eligible for a $${amountStr} medical tax credit 😮\n` +
      `Just snapped receipts for 1 sec each.\n` +
      `Takes 10 seconds to see if you qualify →\n` +
      `${shareUrl}`;

    // Walks the cloned DOM and forces every color/background/border to the
    // resolved rgb() value the browser has already computed. This avoids
    // html2canvas choking on oklch(), color-mix(), or CSS custom props on
    // iOS Safari. Runs on the *cloned* iframe DOM so the live UI is untouched.
    const forceRgbColors = (root) => {
      const CT = window.NodeFilter?.SHOW_ELEMENT ?? 1;
      const walker = root.ownerDocument.createTreeWalker(root, CT);
      const nodes = [root];
      let n = walker.nextNode();
      while (n) { nodes.push(n); n = walker.nextNode(); }
      for (const el of nodes) {
        try {
          const cs = el.ownerDocument.defaultView.getComputedStyle(el);
          if (cs.color) el.style.color = cs.color;
          if (cs.backgroundColor) el.style.backgroundColor = cs.backgroundColor;
          if (cs.borderTopColor)    el.style.borderTopColor    = cs.borderTopColor;
          if (cs.borderRightColor)  el.style.borderRightColor  = cs.borderRightColor;
          if (cs.borderBottomColor) el.style.borderBottomColor = cs.borderBottomColor;
          if (cs.borderLeftColor)   el.style.borderLeftColor   = cs.borderLeftColor;
          if (cs.fill && cs.fill !== "none") el.style.fill = cs.fill;
          if (cs.stroke && cs.stroke !== "none") el.style.stroke = cs.stroke;
        } catch { /* ignore individual node failures */ }
      }
    };

    // Fallback #1: download the PNG so the user still gets the image
    const downloadPng = (blob) => {
      try {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "medeasy-tax-credit.png";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success("Image saved — post it anywhere!");
      } catch {
        toast.error("Could not save the image — try again");
      }
    };

    // Fallback #2: no image at all — deliver text + link only
    const textOnlyFallback = async () => {
      try {
        await navigator.clipboard.writeText(shareText);
        toast.success("Caption copied — paste it with your MedEasy link!");
      } catch {
        toast.error("Could not create share image — try again");
      }
    };

    // ---- Step 1: try to make the image ----
    let blob = null;
    try {
      const canvas = await html2canvas(shareCardRef.current, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
        logging: false,
        onclone: (_doc, clonedEl) => forceRgbColors(clonedEl),
      });
      blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png", 1));
      if (!blob) throw new Error("no blob");
    } catch {
      // html2canvas or toBlob failed (typical on older iOS Safari) — deliver
      // text-only fallback and bail out cleanly. The user still gets a
      // shareable caption + link.
      await textOnlyFallback();
      setSharing(false);
      return;
    }

    const file = new File([blob], "medeasy-tax-credit.png", { type: "image/png" });

    // ---- Step 2: prefer native share sheet, fall back to PNG download ----
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          files: [file],
          title: "I qualify for a CRA medical tax credit",
          text: shareText,
        });
      } catch (err) {
        if (err?.name === "AbortError") {
          // User cancelled — silent no-op
        } else {
          // iOS occasionally rejects after losing the user-gesture token.
          // Fall back to the PNG download so the share still succeeds.
          downloadPng(blob);
        }
      }
    } else {
      // Desktop / older mobile fallback — download PNG and copy caption
      downloadPng(blob);
      try { await navigator.clipboard.writeText(shareText); } catch { /* noop */ }
    }
    setSharing(false);
  };
  const [province, setProvince] = useState("ON");
  const [filingStatus, setFilingStatus] = useState("individual"); // individual | couple
  const [income, setIncome] = useState("");      // primary / spouse A
  const [income2, setIncome2] = useState("");    // spouse B (couple mode)
  const [expenses, setExpenses] = useState("");
  // Tracks which quick-add preset (if any) is currently active. Cleared as
  // soon as the user types a value that diverges from the preset amount.
  const [selectedPreset, setSelectedPreset] = useState(null);
  const EXPENSE_PRESETS = [3000, 5000, 8000, 12000];

  const calc = useMemo(() => {
    // Strip any non-digits (handles "$70,000" or "70,000" pasted values)
    const toNum = (v) => Number(String(v).replace(/[^\d.]/g, "")) || 0;
    const inc1 = toNum(income);
    const inc2 = toNum(income2);
    const exp = toNum(expenses);
    const provInfo = PROVINCIAL_RATES[province] || PROVINCIAL_RATES.ON;
    const combinedRate = FEDERAL_RATE + provInfo.rate;

    // Per-spouse computation: threshold = lesser of (3% × that spouse's income, federal cap)
    // When income is unknown, default to the federal cap (conservative estimate).
    const computeFor = (incomeAmt) => {
      const threshold = incomeAmt > 0 ? Math.min(incomeAmt * 0.03, FEDERAL_CAP_2025) : FEDERAL_CAP_2025;
      const eligible = Math.max(0, exp - threshold);
      const credit = eligible * combinedRate;
      return { income: incomeAmt, threshold, eligible, credit };
    };

    const primary = computeFor(inc1);

    // In couple mode, only run the optimizer when BOTH incomes are known.
    // If only one spouse's income is filled, fall back to a single-claimant calc.
    const isCouple = filingStatus === "couple" && inc1 > 0 && inc2 > 0;

    if (!isCouple) {
      return {
        mode: "individual",
        primary,
        best: primary,
        combinedRate,
        provName: provInfo.name,
        // As soon as expenses are entered we can show an estimate.
        hasInputs: exp > 0,
        incomeKnown: inc1 > 0,
        qualifies: exp > primary.threshold && exp > 0,
        gap: Math.max(0, primary.threshold - exp),
      };
    }

    // Couple mode — score under each spouse, pick the higher credit
    const a = computeFor(inc1);
    const b = computeFor(inc2);
    const optimal = a.credit >= b.credit ? "A" : "B";
    const best = optimal === "A" ? a : b;
    const other = optimal === "A" ? b : a;
    const lowerEarner = inc1 <= inc2 ? "A" : "B";
    const optimalIsLowerEarner = optimal === lowerEarner;

    return {
      mode: "couple",
      a, b, best, other, optimal, optimalIsLowerEarner,
      lowerEarner,
      combinedRate,
      provName: provInfo.name,
      hasInputs: exp > 0,
      incomeKnown: true,
      qualifies: exp > best.threshold && exp > 0,
      gap: Math.max(0, best.threshold - exp),
      uplift: Math.max(0, best.credit - other.credit),
    };
  }, [province, income, income2, expenses, filingStatus]);

  // Lift the current estimate up to the parent (Landing page pricing section)
  // whenever the underlying calc changes.
  useEffect(() => {
    if (typeof onEstimateChange !== "function") return;
    onEstimateChange({
      credit: calc.qualifies ? calc.best.credit : 0,
      qualifies: !!calc.qualifies,
      hasInputs: !!calc.hasInputs,
    });
  }, [calc.qualifies, calc.hasInputs, calc.best.credit, onEstimateChange]);

  return (
    <section className="px-6 sm:px-10 py-16 sm:py-24 bg-[#F7F6F2]" data-testid="tax-calculator-section">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-[#2C4C3B] text-white px-4 py-1.5 rounded-full text-[10px] uppercase tracking-[0.25em] font-semibold">
            <Calculator size={14} weight="duotone" />
            Free instant estimate
          </div>
          <h2 className="font-[Manrope] font-bold text-3xl sm:text-5xl text-[#1E2420] mt-5 tracking-tight">
            Will you qualify?
          </h2>
          <p className="text-[#5C6660] mt-3 text-base sm:text-lg max-w-2xl mx-auto">
            Find out in 10 seconds whether your medical expenses cross the CRA threshold for the medical-expense tax credit — and your estimated tax credit.
          </p>
        </div>

        <Card className="rounded-3xl border-[#E3E1D9] shadow-sm overflow-hidden">
          <CardContent className="p-6 sm:p-10">
            {/* Filing status toggle */}
            <div className="flex items-center gap-2 mb-6 p-1 rounded-full bg-[#EBE9E0] w-fit mx-auto" role="tablist">
              <button
                type="button"
                role="tab"
                data-testid="filing-toggle-individual"
                aria-selected={filingStatus === "individual"}
                onClick={() => setFilingStatus("individual")}
                className={`inline-flex items-center gap-2 px-5 h-10 rounded-full text-sm font-semibold transition-colors ${
                  filingStatus === "individual"
                    ? "bg-white text-[#1E2420] shadow-sm"
                    : "text-[#5C6660] hover:text-[#1E2420]"
                }`}
              >
                <User size={16} weight="duotone" />
                Individual
              </button>
              <button
                type="button"
                role="tab"
                data-testid="filing-toggle-couple"
                aria-selected={filingStatus === "couple"}
                onClick={() => setFilingStatus("couple")}
                className={`inline-flex items-center gap-2 px-5 h-10 rounded-full text-sm font-semibold transition-colors ${
                  filingStatus === "couple"
                    ? "bg-white text-[#1E2420] shadow-sm"
                    : "text-[#5C6660] hover:text-[#1E2420]"
                }`}
              >
                <Users size={16} weight="duotone" />
                Couple
              </button>
            </div>

            {/* Inputs */}
            <div className={`grid grid-cols-1 ${filingStatus === "couple" ? "md:grid-cols-4" : "md:grid-cols-3"} gap-4 sm:gap-5`}>
              <div>
                <Label htmlFor="calc-province" className="text-xs uppercase tracking-widest text-[#5C6660] font-semibold">
                  Province
                </Label>
                <Select value={province} onValueChange={setProvince}>
                  <SelectTrigger
                    id="calc-province"
                    data-testid="calc-province"
                    className="mt-2 h-12 rounded-xl border-[#E3E1D9]"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PROVINCIAL_RATES).map(([code, { name }]) => (
                      <SelectItem key={code} value={code}>{name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="calc-income" className="text-xs uppercase tracking-widest text-[#5C6660] font-semibold">
                  {filingStatus === "couple" ? "Spouse 1 net income" : "Estimated net income"}
                </Label>
                <div className="relative mt-2">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6660] text-sm">$</span>
                  <Input
                    id="calc-income"
                    data-testid="calc-income"
                    type="number"
                    inputMode="numeric"
                    placeholder="e.g. 70000"
                    value={income}
                    onChange={(e) => setIncome(e.target.value)}
                    className="h-12 pl-7 rounded-xl border-[#E3E1D9] text-base placeholder:text-[#B7B3A5]"
                  />
                </div>
              </div>
              {filingStatus === "couple" && (
                <div>
                  <Label htmlFor="calc-income-2" className="text-xs uppercase tracking-widest text-[#5C6660] font-semibold">
                    Spouse 2 net income
                  </Label>
                  <div className="relative mt-2">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6660] text-sm">$</span>
                    <Input
                      id="calc-income-2"
                      data-testid="calc-income-2"
                      type="number"
                      inputMode="numeric"
                      placeholder="e.g. 50000"
                      value={income2}
                      onChange={(e) => setIncome2(e.target.value)}
                      className="h-12 pl-7 rounded-xl border-[#E3E1D9] text-base placeholder:text-[#B7B3A5]"
                    />
                  </div>
                </div>
              )}
              <div>
                <Label htmlFor="calc-expenses" className="text-xs uppercase tracking-widest text-[#5C6660] font-semibold">
                  {filingStatus === "couple" ? "Household out-of-pocket medical" : "Annual out-of-pocket medical"}
                </Label>
                <div className="relative mt-2">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6660] text-sm">$</span>
                  <Input
                    id="calc-expenses"
                    data-testid="calc-expenses"
                    type="number"
                    inputMode="numeric"
                    placeholder="e.g. 3500"
                    value={expenses}
                    onChange={(e) => {
                      setExpenses(e.target.value);
                      // Clear the active pill when the typed value no longer
                      // matches a preset exactly.
                      const typed = Number(e.target.value);
                      setSelectedPreset(EXPENSE_PRESETS.includes(typed) ? typed : null);
                    }}
                    className="h-12 pl-7 rounded-xl border-[#E3E1D9] text-base placeholder:text-[#B7B3A5]"
                  />
                </div>

                {/* Quick-add preset pills — one-tap common amounts.
                    Sets expenses state to the preset numeric value and marks
                    the pill active. Clears on manual typing that diverges. */}
                <div
                  className="mt-3 flex flex-wrap gap-2"
                  data-testid="calc-expense-presets"
                  role="group"
                  aria-label="Quick-add expense amounts"
                >
                  {EXPENSE_PRESETS.map((amt) => {
                    const active = selectedPreset === amt;
                    return (
                      <button
                        type="button"
                        key={amt}
                        data-testid={`calc-preset-${amt}`}
                        aria-pressed={active}
                        onClick={() => {
                          setExpenses(String(amt));
                          setSelectedPreset(amt);
                        }}
                        className={`h-9 px-4 rounded-full border text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-[#2C4C3B]/30 ${
                          active
                            ? "bg-[#2C4C3B] text-white border-[#2C4C3B] shadow-sm"
                            : "bg-white text-[#2C4C3B] border-[#E3E1D9] hover:bg-[#F2EFE6] hover:border-[#2C4C3B]/40"
                        }`}
                      >
                        {`$${amt.toLocaleString("en-CA")}`}
                      </button>
                    );
                  })}
                </div>

                {/* Memory-jog category chips — static, non-interactive. */}
                <div className="mt-4" data-testid="calc-memory-chips">
                  <div className="text-[11px] uppercase tracking-widest text-[#5C6660] font-semibold mb-2">
                    Don&apos;t forget to include:
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      "Prescriptions",
                      "Medical travel (40km+)",
                      "Dental & vision",
                      "Physio/chiro",
                      "Private insurance premiums",
                      "Specialized care",
                    ].map((label) => (
                      <span
                        key={label}
                        data-testid={`calc-memory-chip-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className="inline-flex items-center h-7 px-3 rounded-full bg-[#EBE9E0] text-[#3D4540] text-[12px] leading-none"
                      >
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
                <p className="text-[11px] text-[#5C6660] mt-1.5 leading-snug" data-testid="calc-expenses-hint">
                  Add up <strong className="text-[#1E2420]">everything you paid out of pocket</strong> last year, including:
                  <br />
                  <span className="block mt-1">
                    • <strong className="text-[#1E2420]">Out-of-pocket prescriptions</strong> &mdash; the portion of any drug receipt (with a DIN) that your plan didn&apos;t reimburse
                    <br />
                    • <strong className="text-[#1E2420]">Medical travel</strong> &mdash; mileage on trips 40+ km one-way, plus parking, tolls, meals &amp; accommodation on 80+ km trips
                    <br />
                    • <strong className="text-[#1E2420]">Dental &amp; vision</strong> &mdash; cleanings, fillings, eye exams, glasses, contacts &mdash; the portion your plan didn&apos;t cover
                    <br />
                    • <strong className="text-[#1E2420]">Private health-insurance premiums</strong> (Blue Cross, Sun Life, Manulife) and workplace health deductions on your pay stub
                    <br />
                    • <strong className="text-[#1E2420]">Specialized care</strong> &mdash; physio, chiro, CPAP, orthotics, hearing aids, mental-health services
                  </span>
                  <span className="block mt-1.5 italic">See the Quick Guide below for the full list of what counts.</span>
                </p>
              </div>
            </div>

            {/* Result */}
            <div className="mt-8" data-testid="calc-result">
              {!calc.hasInputs ? (
                <div className="text-center text-sm text-[#5C6660] py-6 border border-dashed border-[#E3E1D9] rounded-2xl">
                  <div className="font-semibold text-[#1E2420] mb-1">Live estimate — no button needed</div>
                  Start by entering your <strong>annual out-of-pocket medical</strong>. We&apos;ll calculate the moment you type.
                </div>
              ) : calc.qualifies ? (
                <div
                  className="rounded-2xl bg-gradient-to-br from-[#2C4C3B] to-[#1E2420] text-white p-7 sm:p-9 relative overflow-hidden"
                  data-testid="calc-result-qualifies"
                >
                  <div className="absolute -top-6 -right-6 opacity-10">
                    <Sparkle size={140} weight="fill" />
                  </div>
                  <div className="relative">
                    <div className="text-[#D4A373] text-[10px] uppercase tracking-[0.3em] font-bold">
                      Boom! You qualify
                    </div>
                    <div className="font-[Manrope] font-bold text-5xl sm:text-6xl mt-3 tracking-tight" data-testid="calc-credit-amount">
                      {fmtCAD(calc.best.credit)}
                    </div>
                    <div className="text-white/80 text-base mt-2">
                      estimated back on your taxes &mdash; from <strong className="text-white">{fmtCAD(calc.best.eligible)}</strong> in eligible medical expenses.
                    </div>

                    {calc.mode === "individual" && !calc.incomeKnown && (
                      <div className="mt-3 text-[12px] text-[#D4A373]" data-testid="calc-income-hint">
                        Tip: add your net income for a more accurate threshold (we&apos;re using the federal cap of ${FEDERAL_CAP_2025} for now).
                      </div>
                    )}

                    {/* Spousal Optimizer educational tip — only in couple mode */}
                    {calc.mode === "couple" && (
                      <div
                        className="mt-5 rounded-xl bg-[#D4A373]/15 border border-[#D4A373]/40 p-4"
                        data-testid="spousal-optimizer-tip"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <Users size={16} weight="duotone" className="text-[#D4A373]" />
                          <span className="text-[#D4A373] text-[10px] uppercase tracking-[0.25em] font-bold">
                            Spousal optimization applied
                          </span>
                        </div>
                        <p className="text-sm text-white/90 leading-relaxed">
                          CRA rules allow couples to pool family medical expenses under either spouse. <strong className="text-white">MedEasy has optimized your claim under the {calc.optimalIsLowerEarner ? "lower-income earner" : "higher-credit spouse"} to maximize your household tax credit.</strong>
                        </p>
                        {calc.uplift > 0 && (
                          <p className="text-xs text-[#D4A373] mt-2" data-testid="spousal-uplift">
                            That&apos;s <strong>{fmtCAD(calc.uplift)} more</strong> than claiming under the other spouse ({fmtCAD(calc.other.credit)}).
                          </p>
                        )}
                      </div>
                    )}

                    <div className="mt-5 pt-5 border-t border-white/15 text-sm leading-relaxed text-white/85">
                      The CRA requires an <strong className="text-white">itemized logbook</strong> with dates, receipts, and DIN/provider details to claim this.
                      <strong className="text-[#D4A373]"> Let MedEasy build your audit trail automatically.</strong>
                    </div>
                    <div className="mt-6 flex flex-wrap gap-3">
                      <Button
                        onClick={goSubscribe}
                        data-testid="calc-cta-qualifies"
                        className="rounded-full bg-[#D4A373] hover:bg-[#C2916A] text-[#1E2420] font-semibold h-12 px-7"
                      >
                        Start tracking with MedEasy
                        <ArrowRight size={18} className="ml-2" />
                      </Button>
                      <Button
                        onClick={shareResult}
                        disabled={sharing}
                        data-testid="calc-share-result"
                        variant="outline"
                        className="rounded-full bg-white/10 hover:bg-white/20 border-white/30 text-white h-12 px-6 backdrop-blur disabled:opacity-50"
                      >
                        {sharing ? (
                          <><Sparkle size={18} weight="duotone" className="mr-2 animate-pulse" /> Creating…</>
                        ) : (
                          <><Share size={18} weight="duotone" className="mr-2" /> Share my result</>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  className="rounded-2xl bg-[#FFF6E8] border border-[#D4A373]/40 p-7 sm:p-9"
                  data-testid="calc-result-below"
                >
                  <div className="text-[#8B5A2B] text-[10px] uppercase tracking-[0.3em] font-bold">
                    Below the CRA threshold
                  </div>
                  <div className="font-[Manrope] font-bold text-2xl sm:text-3xl text-[#1E2420] mt-3 leading-tight" data-testid="calc-below-message">
                    Based on this amount and income, your expenses are below the CRA threshold — no credit this year.
                  </div>
                  <div className="text-[#3D4540] text-base mt-3 leading-relaxed">
                    Try a higher amount or add more eligible expenses.
                    You&apos;re <strong>{fmtCAD(calc.gap)}</strong> away from the {calc.mode === "couple" ? "lower-spouse " : ""}threshold ({fmtCAD(calc.best.threshold)} — the lesser of 3% of {calc.mode === "couple" ? "that spouse's" : "your"} income or the ${FEDERAL_CAP_2025} federal cap).
                  </div>
                  <Button
                    onClick={goSubscribe}
                    data-testid="calc-cta-below"
                    className="mt-6 rounded-full bg-[#2C4C3B] hover:bg-[#243D2F] text-white h-12 px-7"
                  >
                    Start free — log your first trip
                    <ArrowRight size={18} className="ml-2" />
                  </Button>
                </div>
              )}
            </div>

            {/* Fine print */}
            <div className="text-[11px] text-[#5C6660] mt-5 leading-relaxed">
              Estimate only. Final amounts depend on your tax filing, dependants, and other credits — confirm with your accountant. Federal cap updates annually; provincial rates reflect the lowest bracket non-refundable rate.
            </div>
          </CardContent>
        </Card>

        {/* OFF-SCREEN SHARE CARD — captured by html2canvas when user taps "Share my result".
            Positioned far off-viewport so it's painted (required for capture) but never seen.
            Matches the on-screen card design but strips the CTA button + adds a QR code. */}
        {calc.hasInputs && calc.qualifies && (
          <div
            style={{ position: "absolute", left: "-9999px", top: 0, pointerEvents: "none" }}
            aria-hidden="true"
          >
            <div
              ref={shareCardRef}
              data-testid="calc-share-card"
              style={{
                width: 1080,
                background: "linear-gradient(160deg, #2C4C3B 0%, #1E2420 100%)",
                color: "white",
                padding: "72px 64px 64px 64px",
                fontFamily: "Manrope, -apple-system, system-ui, sans-serif",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* Decorative sparkle */}
              <div style={{ position: "absolute", top: -60, right: -60, opacity: 0.08, fontSize: 360, lineHeight: 1 }}>✦</div>

              {/* Eyebrow */}
              <div style={{ color: "#D4A373", fontSize: 18, letterSpacing: "0.3em", fontWeight: 700, textTransform: "uppercase" }}>
                You&apos;re getting
              </div>

              {/* Big number */}
              <div style={{ fontSize: 168, fontWeight: 800, lineHeight: 1, marginTop: 24, letterSpacing: "-0.04em" }}>
                {fmtCAD(calc.best.credit)}
              </div>

              {/* Subtitle — province credit framing added back per latest spec */}
              <div style={{ fontSize: 30, lineHeight: 1.35, marginTop: 28, color: "rgba(255,255,255,0.88)" }}>
                estimated back on your taxes
                <br />
                — from <strong style={{ color: "white" }}>{fmtCAD(calc.best.eligible)}</strong> in eligible medical expenses
              </div>
              <div style={{ fontSize: 20, lineHeight: 1.4, marginTop: 10, color: "rgba(255,255,255,0.65)" }}>
                combined federal + {calc.provName} credit.
              </div>

              {/* Divider */}
              <div style={{ height: 1, background: "rgba(255,255,255,0.18)", margin: "44px 0 36px" }} />

              {/* Value-prop block — replaces CRA-jargon with action language */}
              <div style={{ fontSize: 24, lineHeight: 1.45, color: "rgba(255,255,255,0.9)" }}>
                Snap a receipt. We do the rest.
                <br />
                <strong style={{ color: "#D4A373" }}>MedEasy builds your CRA logbook from 1-second photo snaps.</strong>
              </div>

              {/* Bottom row: subtle watermark + QR code.
                  Meta AI feedback: small text URL bottom-left + QR bottom-right
                  reads as "personal win" not "ad" → IG/TikTok won't downrank. */}
              <div style={{ marginTop: 72, display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 32 }}>
                <div style={{ paddingBottom: 4 }}>
                  <div style={{ fontSize: 18, fontWeight: 600, color: "rgba(255,255,255,0.75)", letterSpacing: "0.02em" }}>
                    mymedeasy.ca
                  </div>
                  <div style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", marginTop: 4, letterSpacing: "0.02em" }}>
                    10% supports Hope Air ✈
                  </div>
                </div>
                <div style={{ background: "white", padding: 14, borderRadius: 14 }}>
                  <QRCodeSVG
                    value="https://mymedeasy.ca?ref=share&utm_medium=calc"
                    size={180}
                    bgColor="#FFFFFF"
                    fgColor="#1E2420"
                    level="M"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Quick Guide — accordion of what counts toward the CRA medical credit.
            Collapsed by default via <details>/<summary> so it doesn't
            interrupt the primary hero → calculator → benefits → price flow. */}
        <details className="mt-8 group" data-testid="quick-guide">
          <summary className="list-none cursor-pointer flex items-center gap-2 select-none">
            <span className="text-xl" role="img" aria-label="clipboard">📋</span>
            <h3 className="font-[Manrope] text-xl font-bold text-[#1E2420]">
              Quick Guide: What Qualifies?
            </h3>
            <span className="ml-auto text-sm font-semibold text-[#2C4C3B] underline underline-offset-4 decoration-[#D4A373]/70 group-open:hidden">
              See what qualifies
            </span>
            <span className="ml-auto text-sm font-semibold text-[#5C6660] hidden group-open:inline">
              Hide
            </span>
          </summary>
          <p className="text-sm text-[#5C6660] mb-4 leading-relaxed mt-3">
            Tap any category to see the CRA rules in plain language. All items below feed directly into the calculator above when you log them inside MedEasy.
          </p>

          <Accordion type="multiple" className="rounded-2xl border border-[#E3E1D9] bg-white divide-y divide-[#EDEAE0] overflow-hidden">
            <AccordionItem value="travel" className="px-5" data-testid="qg-travel">
              <AccordionTrigger className="hover:no-underline py-4">
                <div className="flex items-center gap-3 text-left">
                  <span className="w-9 h-9 rounded-xl bg-[#2C4C3B] flex items-center justify-center shrink-0">
                    <Car size={18} weight="duotone" className="text-white" />
                  </span>
                  <span>
                    <span className="font-[Manrope] font-semibold text-base text-[#1E2420] block">Medical Travel</span>
                    <span className="text-xs text-[#5C6660]">The 40 km / 80 km rule, made simple</span>
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-4 pl-12 text-sm text-[#3D4540] leading-relaxed">
                <ul className="space-y-2 list-disc pl-4 marker:text-[#2C4C3B]">
                  <li><strong className="text-[#1E2420]">Under 40 km one-way:</strong> CRA does not allow any travel expenses for medical purposes.</li>
                  <li><strong className="text-[#1E2420]">40 – 79 km one-way:</strong> mileage (at your provincial simplified rate), parking, tolls, taxi &amp; transit are eligible.</li>
                  <li><strong className="text-[#1E2420]">80 km or more one-way:</strong> everything above <em>plus</em> meals and accommodation while you&apos;re away.</li>
                  <li><strong className="text-[#1E2420]">Companion travel:</strong> if a doctor certifies that you can&apos;t travel alone, your companion&apos;s expenses count too.</li>
                </ul>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="premiums" className="px-5" data-testid="qg-premiums">
              <AccordionTrigger className="hover:no-underline py-4">
                <div className="flex items-center gap-3 text-left">
                  <span className="w-9 h-9 rounded-xl bg-[#D4A373] flex items-center justify-center shrink-0">
                    <ShieldCheck size={18} weight="duotone" className="text-[#1E2420]" />
                  </span>
                  <span>
                    <span className="font-[Manrope] font-semibold text-base text-[#1E2420] block">Private Insurance Premiums</span>
                    <span className="text-xs text-[#5C6660]">Blue Cross, Sun Life, Manulife, workplace plans</span>
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-4 pl-12 text-sm text-[#3D4540] leading-relaxed">
                <ul className="space-y-2 list-disc pl-4 marker:text-[#D4A373]">
                  <li>Premiums you pay for <strong className="text-[#1E2420]">private health, dental, or hospital coverage</strong> qualify — Blue Cross, Sun Life, Manulife, Green Shield, etc.</li>
                  <li><strong className="text-[#1E2420]">Workplace deductions</strong> from your pay stub (look for &ldquo;EHB&rdquo;, &ldquo;Health Care&rdquo;, or &ldquo;Dental&rdquo; on your T4) usually count. Provincial premiums (OHIP, MSP, RAMQ) generally do <em>not</em>.</li>
                  <li>Travel-insurance premiums for medical-only emergency coverage are eligible.</li>
                  <li>Keep your year-end statement from the insurer — it&apos;s the audit document the CRA wants.</li>
                </ul>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="dental" className="px-5" data-testid="qg-dental">
              <AccordionTrigger className="hover:no-underline py-4">
                <div className="flex items-center gap-3 text-left">
                  <span className="w-9 h-9 rounded-xl bg-[#2C4C3B] flex items-center justify-center shrink-0">
                    <Tooth size={18} weight="duotone" className="text-white" />
                  </span>
                  <span>
                    <span className="font-[Manrope] font-semibold text-base text-[#1E2420] block">Dental &amp; Vision</span>
                    <span className="text-xs text-[#5C6660]">The portion your plan didn&apos;t cover</span>
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-4 pl-12 text-sm text-[#3D4540] leading-relaxed">
                <ul className="space-y-2 list-disc pl-4 marker:text-[#2C4C3B]">
                  <li><strong className="text-[#1E2420]">Dental work</strong> — cleanings, fillings, crowns, root canals, extractions, orthodontics, and dentures. Purely cosmetic procedures (e.g. whitening) don&apos;t count.</li>
                  <li><strong className="text-[#1E2420]">Eye exams, prescription glasses, contact lenses,</strong> and laser vision correction.</li>
                  <li>Only the <strong className="text-[#1E2420]">out-of-pocket portion</strong> qualifies — subtract whatever your insurer reimbursed.</li>
                </ul>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="prescriptions" className="px-5" data-testid="qg-prescriptions">
              <AccordionTrigger className="hover:no-underline py-4">
                <div className="flex items-center gap-3 text-left">
                  <span className="w-9 h-9 rounded-xl bg-[#D4A373] flex items-center justify-center shrink-0">
                    <Pill size={18} weight="duotone" className="text-[#1E2420]" />
                  </span>
                  <span>
                    <span className="font-[Manrope] font-semibold text-base text-[#1E2420] block">Prescriptions</span>
                    <span className="text-xs text-[#5C6660]">Every receipt with a DIN counts</span>
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-4 pl-12 text-sm text-[#3D4540] leading-relaxed">
                <ul className="space-y-2 list-disc pl-4 marker:text-[#D4A373]">
                  <li><strong className="text-[#1E2420]">Prescription drugs</strong> dispensed by a licensed pharmacist for a medical condition. The pharmacy receipt shows a DIN (Drug Identification Number) — that&apos;s your proof.</li>
                  <li>Insulin, vaccines, and medical cannabis (with a prescription) qualify.</li>
                  <li>Over-the-counter products generally do <em>not</em> count, even if your doctor recommended them.</li>
                  <li>Track only the <strong className="text-[#1E2420]">out-of-pocket portion</strong> after insurance reimbursement.</li>
                </ul>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="specialized" className="px-5" data-testid="qg-specialized">
              <AccordionTrigger className="hover:no-underline py-4">
                <div className="flex items-center gap-3 text-left">
                  <span className="w-9 h-9 rounded-xl bg-[#2C4C3B] flex items-center justify-center shrink-0">
                    <Wheelchair size={18} weight="duotone" className="text-white" />
                  </span>
                  <span>
                    <span className="font-[Manrope] font-semibold text-base text-[#1E2420] block">Specialized Care &amp; Devices</span>
                    <span className="text-xs text-[#5C6660]">Orthotics, physio, CPAP, hearing aids &amp; more</span>
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-4 pl-12 text-sm text-[#3D4540] leading-relaxed">
                <ul className="space-y-2 list-disc pl-4 marker:text-[#2C4C3B]">
                  <li><strong className="text-[#1E2420]">Custom orthotics &amp; orthopedic footwear</strong> prescribed by a doctor, podiatrist, or chiropodist.</li>
                  <li><strong className="text-[#1E2420]">Physiotherapy, chiropractic, osteopath, naturopath, acupuncture, massage therapy</strong> — eligible when performed by a practitioner authorized in your province. Provincial rules vary; check the CRA list.</li>
                  <li><strong className="text-[#1E2420]">CPAP machines &amp; supplies,</strong> hearing aids and batteries, mobility aids (canes, walkers, wheelchairs), and medical alert systems.</li>
                  <li><strong className="text-[#1E2420]">Mental health</strong> — psychologist and psychotherapist fees count when provided by a regulated professional.</li>
                  <li><strong className="text-[#1E2420]">Service animals</strong> (guide dogs, hearing dogs, autism service dogs) — fees and ongoing care.</li>
                </ul>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <p className="text-xs text-[#5C6660] mt-3 italic">
            Not exhaustive — the CRA&apos;s full list (Form T1, lines 33099 / 33199) covers 100+ items. MedEasy lets you tag any expense with the matching CRA category at log time, so your report is audit-ready.
          </p>
        </details>
      </div>
    </section>
  );
}
